import {Color} from '@benchling/nucleus-ui';
import {createStyles, Group, Text, useMantineTheme} from '@mantine/core';
import React, {useEffect, useRef} from 'react';
import {Node, NodeProps, useUpdateNodeInternals} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {NODE_HOVER_BOX_SHADOW} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

import type {ConnectionRole} from './procedureRunDesignFlowchartConnectionUtils';
import {
  ProcedureRunDesignFlowchartNodeHandle,
  ProcedureRunDesignFlowchartNodeHandleVariant,
  ProcedureRunDesignFlowchartNodeHoverGutters,
} from './ProcedureRunDesignFlowchartNodeHandle';
import {getStatusHeaderColor, getStatusIcon} from './unitOperationStatusVisuals';
import {UnitOperationStatusType} from './useMultiProcedureRunStudyProgress';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE = 'procedureRunDesignFlowchartNode';

export function getProcedureRunDesignFlowchartNodeId({
  conditionIndex,
  replicateIndex,
  methodId,
}: {
  conditionIndex: number;
  replicateIndex: number;
  methodId: string;
}): string {
  return `${conditionIndex}-${replicateIndex}-${methodId}`;
}

/** Matches COMMON_NODE_WIDTH used by BenchlingNode, which is set as the node's rendered width. */
export const FALLBACK_NODE_WIDTH = COMMON_NODE_WIDTH;
/** Estimated height of a node before ReactFlow measures it. Approximates a node with no description. */
export const FALLBACK_NODE_HEIGHT = 50;

export interface ProcedureRunDesignFlowchartNodeData {
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  methodId: string;
  isHovered: boolean;
  isHighlighted: boolean;
  // True when this node's condition is persisted and read-only (e.g. editing an existing procedure run).
  // Drives the lock icon in the node header.
  isLocked: boolean;
  // Leave undefined when statuses should not be rendered
  status?: UnitOperationStatusType;
  // Defaults to 'subtle' (transparent) when omitted.
  handleVariant?: ProcedureRunDesignFlowchartNodeHandleVariant;
  // This node's role in the upstream->active draw affordance. Derived from the methods and active layer.
  connectionRole: ConnectionRole;
  // Whether this node's source handle should stay visible at rest (not just on hover). Edge-dependent,
  // only meaningful when connectionRole === 'source'; a source node always shows its handle on hover.
  showPersistentSourceHandle: boolean;
}

export function createProcedureRunDesignFlowchartNode({
  conditionIndex,
  replicateIndex,
  methodId,
  conditionName,
  replicateNumber,
  conditionDescription,
  isHovered,
  isHighlighted,
  isLocked,
  status,
  connectionRole = null,
  showPersistentSourceHandle = false,
}: {
  conditionIndex: number;
  replicateIndex: number;
  methodId: string;
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  isHovered: boolean;
  isHighlighted: boolean;
  isLocked: boolean;
  status?: UnitOperationStatusType;
  connectionRole?: ConnectionRole;
  showPersistentSourceHandle?: boolean;
}): Node<ProcedureRunDesignFlowchartNodeData> {
  return {
    id: getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId}),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
    data: {
      conditionName,
      replicateNumber,
      conditionDescription,
      methodId,
      isHovered,
      isHighlighted,
      isLocked,
      status,
      connectionRole,
      showPersistentSourceHandle,
    },
    position: {
      x: 0,
      y: 0,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE;
}

export default function ProcedureRunDesignFlowchartNode(
  props: NodeProps<ProcedureRunDesignFlowchartNodeData>
): JSX.Element {
  const {classes} = useStyles();
  const theme = useMantineTheme();
  const {data, id} = props;
  const {isHovered, isHighlighted, isLocked, status, connectionRole, showPersistentSourceHandle} = data;

  // Node-local hover, independent of the method/column-level highlight (`isHovered`). Drives the
  // reveal of the active-layer connection handles (the blue source arrow / white target dot).
  const [isNodeHovered, nodeHoverProps] = useHoverState();

  // A node gains/loses the floating arrow handle only when its role changes (not on hover), so
  // refresh the node's measured handle geometry just then to keep ReactFlow's edge anchoring correct.
  const updateNodeInternals = useUpdateNodeInternals();
  const prevConnectionRoleRef = useRef<ConnectionRole>(connectionRole);
  useEffect(() => {
    if (prevConnectionRoleRef.current !== connectionRole) {
      updateNodeInternals(id);
    }
    prevConnectionRoleRef.current = connectionRole;
  }, [connectionRole, id, updateNodeInternals]);

  const isHoveredOrHighlighted = isHovered || isHighlighted;
  // Hover/highlight tints the whole node while the status color tints only the header.
  const rootBg = isHoveredOrHighlighted ? theme.colors.blue[0] : theme.white;
  const headerBg = isHoveredOrHighlighted ? theme.colors.blue[0] : getStatusHeaderColor(status ?? null);
  const {icon: statusIcon, iconColor: statusIconColor} = getStatusIcon(status ?? null);
  return (
    <BenchlingNode
      isSelectable={true}
      isSelected={isHighlighted}
      w="200px"
      sx={{
        backgroundColor: rootBg,
        ...(isHovered && {boxShadow: NODE_HOVER_BOX_SHADOW}),
      }}
      dataTestAttrs={{
        element: 'root',
        key: props.id,
      }}
      onMouseEnter={nodeHoverProps.onMouseEnter}
      onMouseLeave={nodeHoverProps.onMouseLeave}
      onMouseMove={nodeHoverProps.onMouseMove}
    >
      <ProcedureRunDesignFlowchartNodeHoverGutters />
      {/* Each slot delegates to the flush handle or the active-layer connection handle by role. */}
      <ProcedureRunDesignFlowchartNodeHandle
        nodeId={id}
        handleType="target"
        connectionRole={connectionRole}
        showPersistentSourceHandle={false}
        isNodeHovered={isNodeHovered}
        variant={data.handleVariant}
      />
      <ProcedureRunDesignFlowchartNodeHandle
        nodeId={id}
        handleType="source"
        connectionRole={connectionRole}
        showPersistentSourceHandle={showPersistentSourceHandle}
        isNodeHovered={isNodeHovered}
        variant={data.handleVariant}
      />
      <BenchlingNode.Header
        bg={headerBg}
        {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'header'})}
      >
        <Group spacing="sm" align="flex-start" noWrap={true}>
          <Group spacing={theme.spacing.xs} align="center" noWrap={true} className={classes.nameAndLock}>
            {isLocked && (
              <BenchlingIcon
                icon="lock"
                className={classes.lockIcon}
                {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'lockIcon'})}
              />
            )}
            <Text className={classes.conditionReplicate}>
              {`${data.conditionName} - ${data.replicateNumber}`}
            </Text>
          </Group>
          {statusIcon && (
            <BenchlingIcon
              icon={statusIcon}
              className={classes.statusIcon}
              style={{color: statusIconColor}}
            />
          )}
        </Group>
        {data.conditionDescription ? (
          <Text className={classes.description}>{data.conditionDescription}</Text>
        ) : null}
      </BenchlingNode.Header>
      {/* TODO (BNCH-153699): Include sections with varied parameters */}
    </BenchlingNode>
  );
}

const useStyles = createStyles((theme) => ({
  nameAndLock: {
    flex: 1,
    minWidth: 0,
  },
  conditionReplicate: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  description: {
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  statusIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  lockIcon: {
    width: 8,
    height: 8,
    flexShrink: 0,
    color: Color.GRAY_600,
  },
}));
